import { MutableRefObject } from "react";
import ItemMedia from "./ItemMedia";
import { paletteFor } from "./palette";

interface Category {
  id: number;
  name: string;
  items?: Product[];
}

interface Product {
  id: number;
  name: string;
  price: number;
  description?: string;
  imageUrl?: string;
  modifierSets?: { name: string }[];
}

interface CategorySectionsProps {
  categories: Category[];
  sectionRefs: MutableRefObject<Record<number, HTMLElement | null>>;
  onOpenItem: (product: Product, categoryName: string) => void;
}

const SectionHeader = ({ category }: { category: Category }) => {
  const pal = paletteFor(category.name);
  const count = category.items?.length || 0;
  return (
    <div className="sec-head">
      <div className="sec-head__top">
        <span className="sec-head__swatch" style={{ background: pal.primary }} />
        <span className="sec-head__rule" />
        <span className="sec-head__count">{String(count).padStart(2, "0")}</span>
      </div>
      <h2 className="sec-head__name">{category.name}</h2>
    </div>
  );
};

const ItemRow = ({
  product,
  categoryName,
  onClick,
}: {
  product: Product;
  categoryName: string;
  onClick: () => void;
}) => {
  const modCount = product.modifierSets?.length || 0;
  return (
    <button className="item" onClick={onClick}>
      <div className="item__tile-wrap">
        <ItemMedia
          src={product.imageUrl}
          name={product.name}
          itemKey={String(product.id ?? product.name)}
          categoryName={categoryName}
        />
      </div>
      <div className="item__body">
        <div className="item__head">
          <h3 className="item__name" title={product.name}>
            {product.name}
          </h3>
          <span className="item__price">${product.price?.toFixed(2)}</span>
        </div>
        {product.description && product.description !== "Null" && (
          <p className="item__desc">{product.description}</p>
        )}
        <div className="item__meta">
          {modCount > 0 && (
            <span className="item__more">
              <span className="item__more-dot" aria-hidden="true" />
              {modCount} {modCount === 1 ? "option" : "options"} ›
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

const CategorySections = ({ categories, sectionRefs, onOpenItem }: CategorySectionsProps) => (
  <div className="menu-body menu-container">
    {categories.map((c) => (
      <section
        key={c.id}
        data-cat={c.id}
        ref={(el) => {
          sectionRefs.current[c.id] = el;
        }}
        className="menu-section"
      >
        <SectionHeader category={c} />
        <div className="menu-list">
          {(c.items || []).map((item, idx) => (
            <ItemRow
              key={item.id ?? idx}
              product={item}
              categoryName={c.name}
              onClick={() => onOpenItem(item, c.name)}
            />
          ))}
        </div>
      </section>
    ))}
  </div>
);

export default CategorySections;
